import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fileUrl } from "../../lib/api";
import { Loader, SectionHeading, EmptyState } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { ProgressBar } from "../../components/Progress";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Search, Loader2 } from "lucide-react";

const STATUSES = ["Pre-Construction", "Under Construction", "Final Inspection", "Closing", "Occupied", "Warranty", "Post-Warranty"];

export default function Properties() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(params.get("status") || "All");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ address: "", residence_id: "", community: "", model: "", status: "Pre-Construction" });
  const [busy, setBusy] = useState(false);

  const { data: props = [], isLoading } = useQuery({ queryKey: ["admin-properties"], queryFn: async () => (await api.get("/properties")).data });

  const filtered = props.filter((p) =>
    (status === "All" || p.status === status) &&
    (!search || `${p.address} ${p.residence_id} ${p.community}`.toLowerCase().includes(search.toLowerCase())));

  const create = async () => {
    if (!form.address) { toast.error("Address is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/properties", form);
      qc.invalidateQueries({ queryKey: ["admin-properties"] });
      setOpen(false);
      navigate(`/admin/properties/${data.id}`);
    } catch { toast.error("Failed to create"); }
    finally { setBusy(false); }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="space-y-6 arq-fade-up">
      <SectionHeading overline="Portfolio" title="Properties">
        <Button onClick={() => setOpen(true)} data-testid="add-property"><Plus className="h-4 w-4" />New Property</Button>
      </SectionHeading>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search address, residence, community…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="property-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56" data-testid="property-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="All">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? <EmptyState icon={Building2} title="No properties found" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const img = p.hero_image ? (p.hero_image.startsWith("http") ? p.hero_image : fileUrl(p.hero_image)) : "https://images.pexels.com/photos/8134821/pexels-photo-8134821.jpeg";
            return (
              <button key={p.id} onClick={() => navigate(`/admin/properties/${p.id}`)} data-testid={`property-card-${p.id}`}
                className="text-left bg-arq-card border border-border rounded-md overflow-hidden hover:border-arq-green transition-colors group">
                <div className="aspect-[16/9] overflow-hidden bg-arq-surface"><img src={img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-mono text-xs text-arq-accent">#{p.residence_id}</span>
                    <StatusPill status={p.status === "Under Construction" ? "In Progress" : "Active"} />
                  </div>
                  <p className="font-medium text-arq-ink leading-snug">{p.address}</p>
                  <p className="text-xs text-muted-foreground">{p.community} · {p.model}</p>
                  {p.status === "Under Construction" && <div className="mt-3"><div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">{p.status}</span><span className="text-arq-ink">{p.completion_percentage}%</span></div><ProgressBar value={p.completion_percentage} /></div>}
                  <p className="text-xs text-muted-foreground mt-3">{(p.homeowners || []).length} homeowner{(p.homeowners || []).length === 1 ? "" : "s"}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-property-dialog">
          <DialogHeader><DialogTitle>New Property</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label className="mb-1.5 block">Address *</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="np-address" /></div>
            <div><Label className="mb-1.5 block">Residence ID</Label><Input value={form.residence_id} onChange={(e) => setForm({ ...form, residence_id: e.target.value })} data-testid="np-residence" /></div>
            <div><Label className="mb-1.5 block">Community</Label><Input value={form.community} onChange={(e) => setForm({ ...form, community: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Model</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={busy} data-testid="np-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
